import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { loomApi, type LoomConnection } from "../../lib/loomApi";
import "../../styles/loom.css";

/**
 * 织灵账号 connection panel (LOOM_LIVE L-0). A per-user modal: connect your own
 * loom account (login-proxy) so "Try it yourself" runs as you. Copy is plain;
 * no implementation terms surface to the user (OpenSpec §UI 实施约束).
 */
export function LoomPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const [conn, setConn] = useState<LoomConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setConn(await loomApi.status());
    } catch {
      setConn({ connected: false });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const connected = conn?.connected === true;
  const showForm = editing || (!loading && !connected);
  const canConnect = !busy && loginName.trim() !== "" && password !== "";

  async function onConnect(): Promise<void> {
    setErr("");
    setBusy(true);
    try {
      const next = await loomApi.connect(loginName.trim(), password);
      setConn(next);
      setEditing(false);
      setPassword("");
      setLoginName("");
      window.dispatchEvent(new Event("loom-conn-changed"));
    } catch {
      setErr("登录失败，检查一下账号密码再试。");
    }
    setBusy(false);
  }

  async function onDisconnect(): Promise<void> {
    setBusy(true);
    try {
      await loomApi.disconnect();
      await refresh();
      window.dispatchEvent(new Event("loom-conn-changed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="loom-modal-bg" onClick={onClose}>
      <div className="loom-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="loom-head">
          <h2>织灵账号</h2>
          <button className="loom-x" type="button" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>
        <div className="loom-body">
          {loading ? (
            <div className="loom-note">加载中…</div>
          ) : showForm ? (
            <>
              {connected && <div className="loom-note">重新连接会替换当前登录。</div>}
              <div className="loom-reassure">用你的织灵账号登录。密码只用来登录这一次，不会被保存。</div>
              <div className="loom-field">
                <label htmlFor="loom-login">登录名</label>
                <input
                  id="loom-login"
                  className="loom-in"
                  value={loginName}
                  placeholder="你的织灵登录名"
                  onChange={(e) => {
                    setLoginName(e.target.value);
                    setErr("");
                  }}
                />
              </div>
              <div className="loom-field">
                <label htmlFor="loom-pw">密码</label>
                <input
                  id="loom-pw"
                  className="loom-in"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErr("");
                  }}
                />
              </div>
              {err !== "" && (
                <div className="loom-err" role="alert">
                  {err}
                </div>
              )}
              <div className="loom-actions">
                <button className="loom-btn" type="button" disabled={!canConnect} onClick={() => void onConnect()}>
                  {busy ? "连接中…" : "连接"}
                </button>
                {connected && (
                  <button className="loom-btn ghost" type="button" onClick={() => setEditing(false)}>
                    取消
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="loom-status">
                <span className="ok">已连接</span> · {conn?.loginName}
              </div>
              <div className="loom-actions">
                <button className="loom-btn ghost" type="button" onClick={() => setEditing(true)}>
                  重新连接
                </button>
                <button className="loom-btn danger" type="button" disabled={busy} onClick={() => void onDisconnect()}>
                  断开
                </button>
              </div>
              <div className="loom-note">断开后随时可以再连。</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
